import { useEffect, useState } from 'react';

export interface Countdown {
  /** "2d 04h 18m", or "04h 18m 22s" inside the final hour. */
  label: string;
  /** Milliseconds remaining; zero once the deadline has passed. */
  remainingMs: number;
  hasPassed: boolean;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (n: number) => String(n).padStart(2, '0');

function format(remainingMs: number): string {
  if (remainingMs <= 0) return 'Deadline passed';

  const days = Math.floor(remainingMs / DAY);
  const hours = Math.floor((remainingMs % DAY) / HOUR);
  const minutes = Math.floor((remainingMs % HOUR) / MINUTE);
  const seconds = Math.floor((remainingMs % MINUTE) / SECOND);

  // Seconds only matter once it is genuinely urgent.
  if (remainingMs < HOUR) return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  return `${pad(hours)}h ${pad(minutes)}m`;
}

/**
 * Live countdown to an ISO timestamp.
 *
 * Ticks every second inside the final hour and every 30s before that, so a
 * two-day countdown does not re-render 86,400 times.
 */
export function useCountdown(deadlineAt: string | null): Countdown {
  const target = deadlineAt ? new Date(deadlineAt).getTime() : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target === null) return;

    const tick = () => setNow(Date.now());
    const isUrgent = target - Date.now() < HOUR;
    const interval = setInterval(tick, isUrgent ? SECOND : 30 * SECOND);

    return () => clearInterval(interval);
    // `now` is a dependency so the cadence re-evaluates as the deadline nears.
  }, [target, now]);

  if (target === null || Number.isNaN(target)) {
    return { label: '—', remainingMs: 0, hasPassed: false };
  }

  const remainingMs = Math.max(target - now, 0);

  return { label: format(remainingMs), remainingMs, hasPassed: remainingMs <= 0 };
}
